import type { HTMLAttributes, ReactNode } from 'react';

import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';

import { useSession } from '@/features/auth/useSession';
import { useCountries } from '@/features/locations/useCountries';
import { SCOPES, SCOPE_ORDER } from '@/features/posts/registers';
import type { Country } from '@/lib/schemas/location';

import { FEED_WINDOWS, hasActiveFilters, type FeedWindow } from './filters';
import type { FeedFiltersApi } from './useFeedFilters';

/**
 * Sentinel for the pinned "Global 🌍" option, since a country code of '' is not
 * a country. Its name is filled in from the locale at render time.
 */
const GLOBAL: Country = { code: '', name: 'Global', flagEmoji: '🌍' };

/**
 * Every filter from spec §6.1, all combinable, all reflected in the URL.
 *
 * The country selector pins Global first and the reader's home country second,
 * because those are the two options nearly every session starts from.
 */
export function FeedFilters({
  api,
  showHeading = true,
}: {
  api: FeedFiltersApi;
  /** Off inside the bottom sheet, which has a heading of its own. */
  showHeading?: boolean;
}) {
  const { t } = useTranslation();
  const { filters, setFilters, toggleScope, toggleTag, clear } = api;
  const { data: countries = [] } = useCountries();
  const { user } = useSession();

  const global: Country = { ...GLOBAL, name: t('feed.global') };
  const options = buildCountryOptions(countries, global, user?.homeCountryCode);
  const selectedCountry = options.find((country) => country.code === filters.country) ?? global;

  return (
    <Stack spacing={2.5} component="section" aria-label={t('feed.filters')}>
      {showHeading && (
        <Typography variant="overline" color="text.secondary" component="h2">
          {t('feed.filtersShort')}
        </Typography>
      )}

      <Stack spacing={1.5}>
        <Autocomplete<Country, false, true>
          size="small"
          disableClearable
          options={options}
          value={selectedCountry}
          onChange={(_event, country) => setFilters({ country: country.code })}
          getOptionLabel={(country) => country.name}
          getOptionKey={(country) => country.code}
          isOptionEqualToValue={(option, value) => option.code === value.code}
          renderOption={(props, country) => {
            const { key, ...optionProps } = props as HTMLAttributes<HTMLLIElement> & {
              key: string;
            };
            return (
              <Box component="li" key={key} {...optionProps} sx={{ gap: 1 }}>
                <span aria-hidden>{country.flagEmoji}</span>
                {country.name}
              </Box>
            );
          }}
          renderInput={(params) => <TextField {...params} label={t('feed.place')} />}
        />

        {filters.country && (
          <TextField
            size="small"
            label={t('feed.town')}
            placeholder={t('feed.anyTown')}
            value={filters.town}
            onChange={(event) => setFilters({ town: event.target.value })}
            InputProps={{
              endAdornment: filters.town ? (
                <Chip
                  size="small"
                  label={t('common.clear')}
                  onDelete={() => setFilters({ town: '' })}
                  deleteIcon={<CloseRoundedIcon />}
                />
              ) : undefined,
            }}
          />
        )}
      </Stack>

      <FilterGroup label={t('feed.about')}>
        {SCOPE_ORDER.map((scope) => {
          const definition = SCOPES[scope];
          const selected = filters.scopes.includes(scope);

          return (
            <Chip
              key={scope}
              size="small"
              clickable
              variant={selected ? 'filled' : 'outlined'}
              icon={<definition.Icon fontSize="small" aria-hidden />}
              label={t(`scopes.${scope}.label`)}
              onClick={() => toggleScope(scope)}
              aria-pressed={selected}
            />
          );
        })}
      </FilterGroup>

      {filters.tags.length > 0 && (
        <FilterGroup label={t('feed.tags')}>
          {filters.tags.map((tag) => (
            <Chip
              key={tag}
              size="small"
              label={`#${tag}`}
              onDelete={() => toggleTag(tag)}
              variant="filled"
            />
          ))}
        </FilterGroup>
      )}

      {/* Only shown for "top", which is the one sort that needs a period. */}
      {filters.sort === 'top' && (
        <TextField
          select
          size="small"
          label={t('feed.from')}
          value={filters.window}
          onChange={(event) => setFilters({ window: event.target.value as FeedWindow })}
          fullWidth
        >
          {FEED_WINDOWS.map((window) => (
            <MenuItem key={window} value={window}>
              {t(`feed.windows.${window}`)}
            </MenuItem>
          ))}
        </TextField>
      )}

      {hasActiveFilters(filters) && (
        <Button size="small" variant="text" onClick={clear} startIcon={<CloseRoundedIcon />}>
          {t('feed.clearFilters')}
        </Button>
      )}
    </Stack>
  );
}

function FilterGroup({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Stack spacing={1} component="fieldset" sx={{ border: 0, p: 0, m: 0 }}>
      <Typography variant="overline" component="legend" color="text.secondary">
        {label}
      </Typography>
      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
        {children}
      </Stack>
    </Stack>
  );
}

/** Global first, the reader's home country second, then everything alphabetically. */
function buildCountryOptions(
  countries: Country[],
  global: Country,
  homeCountryCode?: string,
): Country[] {
  const home = countries.find((country) => country.code === homeCountryCode);
  const rest = countries.filter((country) => country.code !== homeCountryCode);

  return home ? [global, home, ...rest] : [global, ...rest];
}
